// frontend/src/pages/MFASetup.js
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getTOTPAuthenticator,
  activateTOTP,
  deactivateTOTP,
  getRecoveryCodes,
  regenerateRecoveryCodes,
} from "../library/allauth";
import { QRCodeSVG } from "qrcode.react";
import Navbar from "../components/navigation/Navbar";
import { Shield, CheckCircle, AlertTriangle } from "lucide-react";
let totpLoaded = false;

export default function MFASetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const isRequired =
    new URLSearchParams(location.search).get("required") === "true";
  const [totp, setTotp] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [showCodes, setShowCodes] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  // const hasLoaded = useRef(false);

  useEffect(() => {
    totpLoaded = false; // Reset on mount so AuthContext calls don't block us
    loadTOTP();
  }, []);

  async function loadTOTP(force = false) {
    if (totpLoaded && !force) return;
    totpLoaded = true;
    setLoading(true);
    try {
      const res = await getTOTPAuthenticator();
      if (res.status === 200) {
        // Clear any cached pending secret
        localStorage.removeItem("mfa_pending_totp");
        setTotp({ activated: true, ...res.data });
      } else if (res.status === 404) {
        if (!force) {
          // Use cached secret if available
          const cached = localStorage.getItem("mfa_pending_totp");
          if (cached) {
            setTotp(JSON.parse(cached));
            setLoading(false);
            return;
          }
        }
        // Cache the new secret
        localStorage.setItem("mfa_pending_totp", JSON.stringify(res.meta));
        setTotp(res.meta);
      }
    } catch (err) {
      console.error("Failed to load TOTP:", err);
    } finally {
      setLoading(false);
    }
  }

  async function handleActivate(e) {
    e.preventDefault();
    setError("");
    setActivating(true);
    try {
      const res = await activateTOTP(code);
      if (res.status === 200) {
        setCode("");
        sessionStorage.removeItem("mfa_setup_totp");
        localStorage.removeItem("mfa_pending_totp");
        setShowSuccessModal(true);
        totpLoaded = false;
        await loadTOTP(true);
      } else {
        setError(res.errors?.[0]?.message || "Invalid code. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setActivating(false);
    }
  }

  async function handleDeactivate() {
    setDeactivating(true);
    setShowDisableConfirm(false);
    try {
      // First reauthenticate with password
      const reauth = await fetch(
        `${window.location.origin}/api/_allauth/browser/v1/auth/reauthenticate`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
            "X-CSRFToken":
              document.cookie.match(/csrftoken=([^;]+)/)?.[1] || "",
          },
          body: JSON.stringify({ password: disablePassword }),
        },
      );
      const reauthData = await reauth.json();
      if (reauthData.status !== 200) {
        setError("Incorrect password. Please try again.");
        setDeactivating(false);
        return;
      }
      // Now disable TOTP
      const res = await deactivateTOTP();
      if (res.status === 200) {
        setDisablePassword("");
        totpLoaded = false;
        await loadTOTP(true);
      } else {
        setError(res.errors?.[0]?.message || "Failed to disable 2FA.");
      }
    } catch {
      setError("Something went wrong.");
    } finally {
      setDeactivating(false);
    }
  }

  async function loadRecoveryCodes() {
    setLoadingCodes(true);
    try {
      const res = await getRecoveryCodes();
      if (res.status === 200) {
        setRecoveryCodes(
          res.data?.unused_codes || res.meta?.unused_codes || [],
        );
        setShowCodes(true);
      }
    } catch (err) {
      console.error("Failed to load recovery codes:", err);
    } finally {
      setLoadingCodes(false);
    }
  }

  async function handleRegenerateCodes() {
    if (
      !window.confirm(
        "Regenerate backup codes? Your old codes will stop working.",
      )
    )
      return;
    setRegenerating(true);
    try {
      const res = await regenerateRecoveryCodes();
      if (res.status === 200) {
        setRecoveryCodes(
          res.data?.unused_codes || res.meta?.unused_codes || [],
        );
        setShowCodes(true);
      }
    } catch (err) {
      console.error("Failed to regenerate:", err);
    } finally {
      setRegenerating(false);
    }
  }
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-14">
        <div className="max-w-xl mx-auto px-4 py-10">
          {/* Success Modal */}
          {showSuccessModal && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
              <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4 shadow-xl text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  2FA Enabled Successfully!
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  {isRequired
                    ? "Your account is protected. Please log in to continue."
                    : "Your account is now protected with two-factor authentication."}
                </p>
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate(isRequired ? "/login" : "/settings");
                  }}
                  className="w-full py-3 text-sm font-medium text-white bg-emerald-700 rounded-lg hover:bg-emerald-800 transition-colors"
                >
                  {isRequired ? "Go to Login" : "Go to Settings"}
                </button>
              </div>
            </div>
          )}

          {/* Disable Confirm Modal */}
          {showDisableConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
              <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4 shadow-xl text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle size={32} className="text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Disable 2FA?
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Enter your password to confirm disabling two-factor
                  authentication.
                </p>
                <input
                  type="password"
                  placeholder="Your password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-left focus:outline-none focus:ring-2 focus:ring-red-400 mb-4"
                  autoFocus
                />
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowDisableConfirm(false);
                      setDisablePassword("");
                    }}
                    className="flex-1 py-3 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeactivate}
                    disabled={deactivating || !disablePassword}
                    className="flex-1 py-3 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {deactivating ? "Disabling..." : "Yes, Disable"}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6 pb-5 border-b border-gray-100">
              <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                <Shield size={20} className="text-emerald-600" />
              </div>

              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Two-Factor Authentication
                </h2>
                <p className="text-sm text-gray-400">
                  Secure your account with an authenticator app
                </p>
              </div>
            </div>
            {isRequired && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 font-medium text-center">
                  ⚠️ You must set up two-factor authentication to access the
                  portal.
                </p>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : totp?.activated ? (
              <div className="space-y-5">
                <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                  <CheckCircle
                    size={18}
                    className="text-emerald-600 flex-shrink-0"
                  />
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">
                      2FA is active
                    </p>
                    <p className="text-xs text-emerald-600 mt-0.5">
                      Your account is protected. To add to a new app, disable
                      and re-setup.
                    </p>
                  </div>
                </div>
                {error && (
                  <div className="p-3 bg-red-50 border border-red-100 rounded-lg">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowDisableConfirm(true)}
                    disabled={deactivating}
                    className="flex-1 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    Disable 2FA
                  </button>
                  <button
                    onClick={() => navigate("/settings")}
                    className="flex-1 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Back to Settings
                  </button>
                </div>

                {/* Recovery Codes Section — outside the buttons row */}
                <div className="border-t border-gray-100 pt-5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-800">
                        Backup Recovery Codes
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Use these if you lose access to your authenticator app.
                      </p>
                    </div>
                    <button
                      onClick={
                        showCodes
                          ? () => setShowCodes(false)
                          : loadRecoveryCodes
                      }
                      disabled={loadingCodes}
                      className="px-3 py-1.5 text-xs font-medium text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-50 transition-colors disabled:opacity-50"
                    >
                      {loadingCodes
                        ? "Loading..."
                        : showCodes
                          ? "Hide Codes"
                          : "View Codes"}
                    </button>
                  </div>
                  {showCodes && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-2 p-4 bg-gray-50 rounded-xl border border-gray-100">
                        {recoveryCodes.length > 0 ? (
                          recoveryCodes.map((c, i) => (
                            <code
                              key={i}
                              className="text-xs font-mono text-gray-700 bg-white px-3 py-2 rounded-lg border border-gray-200 text-center"
                            >
                              {c}
                            </code>
                          ))
                        ) : (
                          <p className="text-xs text-gray-400 col-span-2 text-center py-2">
                            No unused codes remaining.
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            const text = recoveryCodes.join("\n");
                            const blob = new Blob([text], {
                              type: "text/plain",
                            });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = "xroadz-recovery-codes.txt";
                            a.click();
                          }}
                          className="flex-1 py-2 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Download Codes
                        </button>
                        <button
                          onClick={handleRegenerateCodes}
                          disabled={regenerating}
                          className="flex-1 py-2 text-xs font-medium text-orange-600 border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors disabled:opacity-50"
                        >
                          {regenerating
                            ? "Regenerating..."
                            : "Regenerate Codes"}
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 text-center">
                        Save these codes somewhere safe. Each code can only be
                        used once.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-6 h-6 rounded-full bg-emerald-700 text-white text-xs font-bold flex items-center justify-center">
                      1
                    </span>
                    <p className="text-sm font-semibold text-gray-800">
                      Scan QR Code
                    </p>
                  </div>
                  <p className="text-sm text-gray-500 mb-4 ml-8">
                    Open Google Authenticator, Authy, or any TOTP app and scan
                    this code.
                  </p>
                  {totp?.totp_url ? (
                    <div className="flex justify-center py-4 bg-gray-50 rounded-xl border border-gray-100">
                      <div className="p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
                        <QRCodeSVG value={totp.totp_url} size={180} />
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-center py-8 bg-gray-50 rounded-xl border border-gray-100">
                      <p className="text-xs text-gray-400 italic">
                        QR code unavailable — try refreshing.
                      </p>
                    </div>
                  )}
                  {totp?.secret && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <p className="text-xs text-gray-400 mb-1.5">
                        Or enter this key manually:
                      </p>
                      <code className="text-xs font-mono text-gray-700 tracking-widest break-all">
                        {totp.secret}
                      </code>
                    </div>
                  )}
                </div>

                <form onSubmit={handleActivate}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-6 h-6 rounded-full bg-emerald-700 text-white text-xs font-bold flex items-center justify-center">
                      2
                    </span>
                    <p className="text-sm font-semibold text-gray-800">
                      Enter Verification Code
                    </p>
                  </div>
                  <p className="text-sm text-gray-500 mb-4 ml-8">
                    Enter the 6-digit code shown in your authenticator app.
                  </p>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-4 text-center text-3xl font-mono tracking-[0.5em] text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent placeholder-gray-300 transition-colors"
                    autoFocus
                  />
                  {error && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-100 rounded-lg">
                      <p className="text-sm text-red-600 text-center">
                        {error}
                      </p>
                    </div>
                  )}
                  <div className="flex gap-3 mt-4">
                    {!isRequired && (
                      <button
                        type="button"
                        onClick={() =>
                          navigate(isRequired ? "/login" : "/settings")
                        }
                        className="flex-1 py-3 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      type="submit"
                      disabled={activating || code.length !== 6}
                      className="flex-1 py-3 text-sm font-semibold text-white bg-emerald-700 rounded-lg hover:bg-emerald-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {activating ? "Activating..." : "Activate 2FA"}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
